import React, { useEffect, useRef, useState } from "react";
import { loadModels, getFaceEmbedding } from "../../../utils/faceUtils.js";
import {
  registerTeacherFace,
  markAttendance,
  fetchTeacherHistory,
  fetchTeacherFace,
} from "../../../service/api.js";
import TeacherPanel from "../panel/TeacherPanel";
import "../styles/TeacherAttendancePage.css";
import api from "../../../api/api";

export default function TeacherAttendancePage() {
  const [status, setStatus] = useState("");
  const [history, setHistory] = useState([]);
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [showAttendanceUI, setShowAttendanceUI] = useState(false);
  const [faceRegistered, setFaceRegistered] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null); // to store camera stream

  useEffect(() => {
    if (!showAttendanceUI) return;

    const init = async () => {
      try {
        setStatus("Starting camera...");
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        streamRef.current = stream; // save stream reference
        if (videoRef.current) videoRef.current.srcObject = stream;

        setStatus("Loading face-api models...");
        await loadModels();
        setModelsLoaded(true);
        setStatus("✅ Models loaded. Ready to detect faces.");

        // Check if face is already registered
        const faceData = await fetchTeacherFace();
        setFaceRegistered(faceData?.registered || false);

        loadHistory();
      } catch (err) {
        console.error("❌ Initialization error:", err);
        setStatus("❌ Unable to initialize camera or models");
      }
    };

    init();

    return () => {
      // Stop camera when component unmounts or UI is hidden
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    };
  }, [showAttendanceUI]);

  const handleRegister = async () => {
    if (!modelsLoaded) return setStatus("❌ Models not loaded yet");

    setStatus("Detecting face for registration...");
    const embedding = await getFaceEmbedding(videoRef.current);
    if (!embedding) return setStatus("❌ No face detected");

    try {
      await registerTeacherFace(embedding);
      setStatus("✅ Face registered successfully");
      setFaceRegistered(true);
    } catch (err) {
      console.error(err);
      setStatus("❌ Registration failed");
    }
  };

  const handleAttendance = async () => {
    if (!modelsLoaded) return setStatus("❌ Models not loaded yet");

    setStatus("Detecting face for attendance...");
    const embedding = await getFaceEmbedding(videoRef.current);
    if (!embedding) return setStatus("❌ No face detected");

    try {
      await markAttendance(embedding);
      setStatus("✅ Attendance marked");
      loadHistory();
    } catch (err) {
      console.error(err);
      setStatus("❌ Attendance failed");
    }
  };

  const loadHistory = async () => {
    try {
      const data = await fetchTeacherHistory();
      setHistory(data);
    } catch (err) {
      console.error("❌ Failed to fetch history:", err);
    }
  };

  const handleBack = () => {
    // Stop camera
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setShowAttendanceUI(false);
    setStatus("");
  };

  return (
    <>
      <TeacherPanel onToggle={setIsSidebarCollapsed} />
      <div className={`ta-main-container ${isSidebarCollapsed ? "ta-collapsed" : ""}`}>
        <h2>Teacher Attendance</h2>

        {!showAttendanceUI && (
          <div className="start-attendance">
            <button
              className="start-attendance-btn"
              onClick={() => setShowAttendanceUI(true)}
            >
              Give Attendance
            </button>
          </div>
        )}

        {showAttendanceUI && (
          <>
            <div className="video-wrapper">
              <video ref={videoRef} id="camera" autoPlay muted className="camera-video" />
            </div>

            <div className="action-buttons">
              {!faceRegistered && (
                <button className="register-btn" onClick={handleRegister} disabled={!modelsLoaded}>
                  Register Face
                </button>
              )}
              <button className="attendance-btn" onClick={handleAttendance} disabled={!modelsLoaded}>
                Mark Attendance
              </button>
              <button className="back-btn" onClick={handleBack}>
                Back
              </button>
            </div>

            <p className="status-text">{status}</p>

            <div className="attendance-history">
              <h3>Attendance History</h3>
              <ul>
                {history.map((h, i) => (
                  <li key={i}>
                    {h.attendance_date} — {h.status}
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
      </div>
    </>
  );
}
